import React from 'react';
import Head from 'next/head';

const TermsPage: React.FC = () => {
  return (
    <>
      <Head>
        <title>Website T&C - Nestflo</title>
        <meta
          name="description"
          content="Website Terms and Conditions of Use for Nestflo"
        />
      </Head>

      {/* Header */}
      <header
        className="shadow-sm"
        style={{ backgroundColor: 'rgb(18, 82, 133)', height: '96px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0">
          <div className="flex justify-between items-center py-4"></div>
        </div>
      </header>

      {/* Main Content */}
      <main
        className="!bg-white min-h-screen"
        style={{ color: 'rgb(18, 82, 133)' }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Website T&C
            </h1>
            <p className="text-xl text-gray-700 mb-2">
              Kunpro Ltd, trading as Nestflo
            </p>
            <p className="text-lg text-gray-600 mb-2">
              Website Terms and Conditions of Use
            </p>
            <p className="text-lg text-gray-600">June 2025</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              1. Introduction
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>1.1</strong> These terms and conditions
                (&ldquo;Terms&rdquo;) govern the relationship between you, the
                User of the website https://nestflo.ai and all its subdomains
                (collectively, the &ldquo;Website&rdquo;), and Kunpro Ltd
                (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
                &ldquo;our&rdquo;), the owner and operator of this Website.
                Kunpro Ltd is a limited company registered in England and Wales
                under company number 13908049, with its registered office at
                Unit 111, Filwood Green Business Park, 1 Filwood Park Lane,
                Bristol, England, BS4 1ET.
              </p>

              <p>
                <strong>1.2</strong> Please read these Terms carefully before
                using our Website. By accessing or using the Website, you
                acknowledge that you have read, understood, and agree to be
                bound by these Terms, which affect your legal rights and
                obligations.
              </p>

              <p>
                <strong>1.3</strong> If you have any questions, comments, or
                concerns regarding these Terms, please contact us at:
                <br />
                • Phone: 0330 133 8626
                <br />• Email: hello@nestflo.ai
              </p>

              <p>
                <strong>1.4</strong> Your agreement to comply with and be bound
                by these terms and conditions is deemed to occur upon your first
                use of the Website. If you do not agree to be bound by these
                terms and conditions, you should stop using the Website
                immediately.
              </p>

              <p>
                <strong>1.5</strong> In these terms and conditions, User or
                Users means any third party that accesses the Website and is not
                either (i) employed by Kunpro Ltd and acting in the course of
                their employment or (ii) engaged as a consultant or otherwise
                providing services to Kunpro Ltd and accessing the Website in
                connection with the provision of such services.
              </p>

              <p>
                <strong>1.6</strong> You must be at least 18 years of age to use
                this Website. By using the Website and agreeing to these terms
                and conditions, you represent and warrant that you are at least
                18 years of age.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              2. Intellectual property and acceptable use
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>2.1</strong> In these terms and conditions, Content
                means any text, graphics, images, audio, video, software data
                compilations, page layout, underlying code and software and any
                other form of information capable of being stored in a computer
                that appears on or forms part of this Website, including any
                such content uploaded by Users.
              </p>

              <p>
                <strong>2.2</strong> All Content included on the Website, unless
                uploaded by Users, is the property of Kunpro Ltd, our affiliates
                or other relevant third parties. By continuing to use the
                Website you acknowledge that such Content is protected by
                copyright, trademarks, database rights and other intellectual
                property rights.
              </p>

              <p>
                <strong>2.3</strong> Nothing on this Website shall be construed
                as granting, by implication, estoppel, or otherwise any license
                or right to use any trademark, logo or service mark displayed on
                this Website without the owner&rsquo;s prior written permission.
              </p>

              <p>
                <strong>2.4</strong> You may, for your own personal,
                non-commercial use only, do the following:
                <br />
                • retrieve, display and view the Content on a device;
                <br />
                • download and store the Content in electronic form on a disk
                (but not on any server or other storage device connected to a
                network); and
                <br />• print one copy of the Content.
              </p>

              <p>
                <strong>2.5</strong> You must not otherwise reproduce, modify,
                copy, distribute or use for commercial purposes any Content
                without the written permission of Kunpro Ltd.
              </p>

              <p>
                <strong>2.6</strong> You acknowledge that you are responsible
                for any Content you may submit via the Website including the
                legality, reliability, appropriateness, originality and
                copyright of any such Content.
              </p>

              <p>
                <strong>2.7</strong> You may not upload to, distribute or
                otherwise publish through the Website any Content that (i) is
                confidential, proprietary, false, fraudulent, libellous,
                defamatory, obscene, threatening, invasive of privacy or
                publicity rights, infringing on intellectual property rights,
                abusive, illegal or otherwise objectionable; (ii) may constitute
                or encourage a criminal offence, violate the rights of any party
                or otherwise give rise to liability or violate any law; or (iii)
                may contain software viruses, political campaigning, chain
                letters, mass mailings, or any form of &ldquo;spam&rdquo;.
              </p>

              <p>
                <strong>2.8</strong> You may not use a false email address or
                other identifying information, impersonate any person or entity
                or otherwise mislead as to the origin of any content.
              </p>

              <p>
                <strong>2.9</strong> You may not upload commercial content onto
                the Website.
              </p>

              <p>
                <strong>2.10</strong> You represent and warrant that you own or
                otherwise control all the rights to the Content you post; that
                the Content is accurate, that use of the Content you supply does
                not violate any provision of these terms and conditions and will
                not cause injury to any person; and that you will indemnify
                Kunpro Ltd for all claims resulting from Content you supply.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              3. Prohibited use
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>3.1</strong> You may not use the Website for any of the
                following purposes:
                <br />
                • in any way which causes, or may cause, damage to the Website
                or interferes with any other person&rsquo;s use or enjoyment of
                the Website;
                <br />
                • in any way which is harmful, unlawful, illegal, abusive,
                harassing, threatening or otherwise objectionable or in breach
                of any applicable law, regulation, governmental order;
                <br />• making, transmitting or storing electronic copies of
                Content protected by copyright without the permission of the
                owner.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              4. Links to other websites
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>4.1</strong> This Website may contain links to other
                sites. Unless expressly stated, these sites are not under the
                control of Kunpro Ltd or that of our affiliates.
              </p>

              <p>
                <strong>4.2</strong> We assume no responsibility for the content
                of such Websites and disclaim liability for any and all forms of
                loss or damage arising out of the use of them.
              </p>

              <p>
                <strong>4.3</strong> The inclusion of a link to another site on
                this Website does not imply any endorsement of the sites
                themselves or of those in control of them.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              5. Privacy Policy and Cookies Policy
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>5.1</strong> Use of the Website is also governed by our
                Privacy Policy and Cookies Policy, which are incorporated into
                these terms and conditions by this reference.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              6. Availability of the Website and disclaimers
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>6.1</strong> Any online facilities, tools, services or
                information that Kunpro Ltd makes available through the Website
                (the Service) is provided &ldquo;as is&rdquo; and on an
                &ldquo;as available&rdquo; basis. We give no warranty that the
                Service will be free of defects and/or faults. To the maximum
                extent permitted by the law, we provide no warranties (express
                or implied) of fitness for a particular purpose, accuracy of
                information compatibility and satisfactory quality. Kunpro Ltd
                is under no obligation to update information on the Website.
              </p>

              <p>
                <strong>6.2</strong> Whilst Kunpro Ltd uses reasonable
                endeavours to ensure that the Website is secure and free of
                errors, viruses and other malware, we give no warranty or
                guaranty in that regard and all Users take responsibility for
                their own security, that of their personal details and their
                computers.
              </p>

              <p>
                <strong>6.3</strong> Kunpro Ltd accepts no liability for any
                disruption or non-availability of the Website. Kunpro Ltd
                reserves the right to alter, suspend or discontinue any part (or
                the whole) of the Website including, but not limited to, any
                products and/or services available.
              </p>

              <p>
                <strong>6.4</strong> These terms and conditions shall continue
                to apply to any modified version of the Website unless it is
                expressly stated otherwise.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              7. Limitation of liability
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>7.1</strong> Nothing in these terms and conditions will:
                <br />
                • limit or exclude our or your liability for death or personal
                injury resulting from our or your negligence, as applicable;
                <br />
                • limit or exclude our or your liability for fraud or fraudulent
                misrepresentation; or
                <br />• limit or exclude any of our or your liabilities in any
                way that is not permitted under applicable law.
              </p>

              <p>
                <strong>7.2</strong> We will not be liable to you in respect of
                any losses arising out of events beyond our reasonable control.
              </p>

              <p>
                <strong>7.3</strong> To the maximum extent permitted by law,
                Kunpro Ltd accepts no liability for any of the following:
                <br />
                • any business losses, such as loss of profits, income, revenue,
                anticipated savings, business contracts, goodwill or commercial
                opportunities;
                <br />
                • loss or corruption of any data, database or software;
                <br />• any special, indirect or consequential loss or damage.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              8. General
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>8.1</strong> You may not transfer any of your rights
                under these terms and conditions to any other person. We may
                transfer our rights under these terms and conditions where we
                reasonably believe your rights will not be affected.
              </p>

              <p>
                <strong>8.2</strong> These terms and conditions may be varied by
                us from time to time. Such revised terms will apply to the
                Website from the date of publication. Users should check the
                terms and conditions regularly to ensure familiarity with the
                then current version.
              </p>

              <p>
                <strong>8.3</strong> These terms and conditions, together with
                the Privacy Policy and Cookies Policy, contain the whole
                agreement between the parties relating to its subject matter and
                supersede all prior discussions, arrangements or agreements that
                might have taken place in relation to the terms and conditions.
              </p>

              <p>
                <strong>8.4</strong> The Contracts (Rights of Third Parties) Act
                1999 shall not apply to these terms and conditions and no third
                party will have any right to enforce or rely on any provision of
                these terms and conditions.
              </p>

              <p>
                <strong>8.5</strong> If any court or competent authority finds
                that any provision of these terms and conditions (or part of any
                provision) is invalid, illegal or unenforceable, that provision
                or part-provision will, to the extent required, be deemed to be
                deleted, and the validity and enforceability of the other
                provisions of these terms and conditions will not be affected.
              </p>

              <p>
                <strong>8.6</strong> Unless otherwise agreed, no delay, act or
                omission by a party in exercising any right or remedy will be
                deemed a waiver of that, or any other, right or remedy.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">
              9. Governing law and jurisdiction
            </h2>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong>9.1</strong> This Agreement shall be governed by and
                interpreted according to the law of England and Wales and all
                disputes arising under the Agreement (including non-contractual
                disputes or claims) shall be subject to the exclusive
                jurisdiction of the English and Welsh courts.
              </p>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default TermsPage;
